/** Spawning a soldier: his duty, his kit, and the temperament that will colour every choice he makes (02 §9.4). */

import { ENEMY, SPECIALS, VEHICLES } from '../tuning';
import type { Enemy, Persona, SpawnDef, VehicleState } from '../types';
import { dogFields } from './specials/dogState';
import { isAutomated, isDog, isVehicle } from './specials/kinds';

/** R4: a vehicle carries its run, its passengers and its progress with it, so a save restores it mid-road. */
function vehicleState(spawn: SpawnDef): VehicleState {
  const route = spawn.route ?? { points: { points: [], width: 4 }, speed: VEHICLES.speed, loop: false };
  return {
    route,
    node: 0,
    reversing: false,
    aboard: spawn.carries ?? 0,
    // ROUND8: a dog never rides (ROUND8-contracts §1): a lorry told to carry dogs carries riflemen.
    aboardKind: spawn.carriesKind === undefined || isDog(spawn.carriesKind) ? 'rifleman' : spawn.carriesKind,
    targetAboard: spawn.carriesTarget === true,
    ...(spawn.dismountAt ? { dismountAt: { ...spawn.dismountAt } } : {}),
    stalledTicks: 0,
    gone: false,
    wrecked: false,
  };
}

export function rollPersona(kind: SpawnDef['kind'], roll: number): Persona {
  if (kind === 'officer') return 'cautious';
  // HAZ: the flamethrower officer closes and burns, never ducks or runs; a machine has no temperament to speak of.
  if (kind === 'flamethrower') return 'aggressive';
  if (isAutomated(kind) || isDog(kind)) return 'steady';
  const table: ReadonlyArray<readonly [Persona, number]> = kind === 'elite'
    ? [['steady', 0.5], ['aggressive', 0.4], ['cautious', 0.1]]
    : [['steady', 0.4], ['aggressive', 0.2], ['cautious', 0.25], ['jumpy', 0.15]];
  let acc = 0;
  for (const [persona, weight] of table) {
    acc += weight;
    if (roll < acc) return persona;
  }
  return 'steady';
}

/** `roll` and `grenadeRoll` are uniform [0,1) draws from the mission's seeded stream. */
export function createEnemy(id: number, spawn: SpawnDef, slot: number, roll: number, grenadeRoll: number, threat: number): Enemy {
  const patrol = spawn.patrol ?? [];
  const duty = patrol.length > 1 ? 'patrol' : 'guard';
  // HAZ: machines sit in 'down', the state all infantry code ignores, behind armour no `damageEnemy` gets through
  // (enemies/specials). A rocket trooper's `grenades` is the rocket in his tube; nobody else special carries any.
  const machine = isAutomated(spawn.kind);
  // ROUND8: a dog sits in 'down' as the machines do - the one state all infantry code leaves alone, so it hears no
  // footstep, answers no shout, rescues nobody, is sent nowhere and takes no prisoner (specials/dog.ts). It carries
  // no grenade, and nothing in its pouches for Salvager: it is `salvaged` from the start and is simply dragged.
  const dog = isDog(spawn.kind);
  const special = machine || dog || spawn.kind === 'flamethrower' || spawn.kind === 'rocketTrooper';
  const grenades = special ? (spawn.kind === 'rocketTrooper' ? 1 : 0)
    : threat >= 2 && spawn.mans === undefined && grenadeRoll < 0.6 ? 1 : 0;
  return {
    // Only when true, so an ordinary soldier serialises exactly as he always has.
    ...(spawn.unarmed === true ? { unarmed: true } : {}),
    ...(isVehicle(spawn.kind) ? { vehicle: vehicleState(spawn) } : {}),
    ...(dog ? dogFields(spawn) : {}),
    id,
    kind: spawn.kind,
    x: spawn.x, z: spawn.z, prevX: spawn.x, prevZ: spawn.z,
    facing: spawn.facing,
    state: machine || dog ? 'down' : duty,
    hp: machine ? SPECIALS.armourHp : ENEMY.hp[spawn.kind],
    detection: 0,
    seesRanger: false,
    wary: false,
    homeX: spawn.x, homeZ: spawn.z, homeFacing: spawn.facing,
    patrol,
    patrolIndex: 0,
    targetX: spawn.x, targetZ: spawn.z,
    path: [], pathIndex: 0,
    stateTicks: 0,
    burstLeft: 0,
    fireCooldown: 0,
    travelled: 0,
    moving: false,
    sinceShot: 999,
    mans: spawn.mans ?? null,
    thinkSlot: slot % ENEMY.perceptionEveryTicks,
    lostTicks: 999,
    duty,
    found: false,
    reactTicks: 0,
    persona: rollPersona(spawn.kind, roll),
    role: 'hold',
    tacticTicks: 0,
    suppressTicks: 0,
    // Riflemen carry a grenade from threat 2 up (02 §9.1).
    grenades: spawn.unarmed === true ? 0 : grenades,
    spotX: spawn.x, spotZ: spawn.z, hasSpot: false,
    peeking: false,
    runnerPost: -1,
    barkTicks: 0,
    carryId: -1,
    carriedBy: -1,
    stabilised: false,
    draggedByRanger: false,
    salvaged: dog,
    selfDestroyed: false,
  };
}
